import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from '../../../utils/debounce';
import { Photo } from '../../../graphql/types';

export enum SlideRenderType {
  photo = 'photo',
  banner = 'banner'
}

export type SlideType =
  | { type: SlideRenderType.photo; photo: Photo; index: number }
  | { type: SlideRenderType.banner };

const BANNER_OFFSET = 3;

export const usePageNumberOnScroll = (
  photos?: Photo[] | null,
  slideRef?: React.RefObject<HTMLDivElement>,
  scrollRef?: React.RefObject<HTMLDivElement>,
  initialGalleryIndex = 1,
  isBannerEnabled = false
) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [isBannerPosition, setIsBannerPosition] = useState(false);
  const [isBannerActivated, setIsBannerActivated] = useState(false);
  const [shouldRenderBanner, setShouldRenderBanner] = useState(false);

  const bannerIndex = useMemo(() => {
    if (!photos || photos.length === 0) return -1;
    return photos.length > 10 ? 10 : photos.length + 1;
  }, [photos]);

  const photosToRender = useMemo(() => {
    if (!photos || photos.length === 0) return [];
    return [photos[photos.length - 1], ...photos, photos[0]];
  }, [photos]);

  const slidesToRender: SlideType[] = useMemo(() => {
    if (!isBannerEnabled) {
      return photosToRender.map((photo, index) => ({
        type: SlideRenderType.photo,
        photo,
        index
      }));
    }

    return photosToRender.flatMap((photo, i) => {
      const result: SlideType[] = [];
      if (i === bannerIndex) result.push({ type: SlideRenderType.banner });
      result.push({ type: SlideRenderType.photo, photo, index: i });
      return result;
    });
  }, [photosToRender, bannerIndex, isBannerEnabled]);

  const slidesToScroll = useMemo(() => {
    return shouldRenderBanner
      ? slidesToRender
      : slidesToRender.filter(slide => slide.type !== SlideRenderType.banner);
  }, [slidesToRender, shouldRenderBanner, isBannerActivated]);

  const scrollToPhoto = (index: number) => {
    if (!scrollRef?.current || !slideRef?.current) return;
    const slideWidth = slideRef.current.offsetWidth;

    const realIndex = Math.max(Math.min(index, slidesToScroll.length - 1), 0);
    scrollRef.current.scrollTo({ left: realIndex * slideWidth });
  };

  const updatePageNumber = () => {
    if (
      !scrollRef?.current ||
      !slideRef?.current ||
      slidesToScroll.length === 0
    )
      return;

    const slideWidth = slideRef.current.offsetWidth;
    const rawIndex = Math.round(scrollRef.current.scrollLeft / slideWidth);

    const currentSlide = slidesToScroll[rawIndex];

    if (!currentSlide) return;

    if (currentSlide.type === SlideRenderType.banner) {
      setIsBannerPosition(true);
      return;
    }

    setIsBannerPosition(false);

    const shouldActivateBanner =
      !isBannerActivated && Math.abs(rawIndex - bannerIndex) <= BANNER_OFFSET;

    if (shouldActivateBanner) {
      setIsBannerActivated(true);
      setShouldRenderBanner(true);
    }

    const photoIndex = currentSlide.index ?? 0;

    if (rawIndex === 0) {
      scrollToPhoto(photos.length);
    } else if (rawIndex === slidesToScroll.length - 1) {
      scrollToPhoto(1);
    } else {
      setPageNumber(photoIndex);
    }
  };

  const debouncedUpdatePageNumber = useCallback(
    debounce(updatePageNumber, 100),
    [slidesToScroll.length]
  );

  useEffect(() => {
    const element = scrollRef?.current;
    if (!element) return;

    const handleScroll = () => {
      debouncedUpdatePageNumber();
    };

    element.addEventListener('scroll', handleScroll);

    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, [debouncedUpdatePageNumber]);

  useEffect(() => {
    if (!slideRef?.current) return;

    const observer = new ResizeObserver(() => {
      if (slideRef.current?.offsetWidth) {
        scrollToPhoto(initialGalleryIndex);
      }
    });

    observer.observe(slideRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollToNextPhoto = () => {
    const slideWidth = slideRef?.current?.offsetWidth ?? 0;
    const currentLeft = scrollRef?.current?.scrollLeft ?? 0;
    const rawIndex = Math.round(currentLeft / slideWidth);

    let nextIndex = rawIndex + 1;

    if (shouldRenderBanner && nextIndex >= slidesToScroll.length) {
      nextIndex = 1;
    }

    if (!shouldRenderBanner && nextIndex >= slidesToScroll.length - 1) {
      nextIndex = 1;
    }

    scrollToPhoto(nextIndex);
  };

  const scrollToPrevPhoto = () => {
    if (!scrollRef?.current || !slideRef?.current) return;

    const slideWidth = slideRef.current.offsetWidth;
    const currentLeft = scrollRef.current.scrollLeft;
    const currentIndex = Math.round(currentLeft / slideWidth);

    let prevIndex = currentIndex - 1;

    if (!shouldRenderBanner && prevIndex <= 0) {
      prevIndex = slidesToScroll.length - 2;
    }

    if (shouldRenderBanner && prevIndex <= 0) {
      prevIndex = slidesToScroll.length - 2;
    }

    scrollToPhoto(prevIndex);
  };

  const isNextBanner =
    pageNumber === bannerIndex - 1 ||
    pageNumber === bannerIndex + 1 ||
    (pageNumber === 1 && bannerIndex === slidesToRender.length - 2);

  return {
    slidesToRender,
    pageNumber,
    scrollToPhoto,
    scrollToNextPhoto,
    scrollToPrevPhoto,
    scrollRef,
    isBannerPosition: shouldRenderBanner ? isBannerPosition : false,
    isBannerActivated,
    shouldRenderBanner,
    isNextBanner,
    setShouldRenderBanner
  };
};
